/**
 * client/src/pages/auth-entry/types.ts
 * Form state and phase types for the AuthEntry page.
 */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

// ---------------------------------------------------------------------------
// Mode / step state machine
// ---------------------------------------------------------------------------

export type AuthMode = "login" | "register";
export type AuthStep = "form" | "ceremony" | "deriving" | "priming" | "complete";
export type AalLevel = "AAL1" | "AAL2" | "AAL3";

// ---------------------------------------------------------------------------
// API response shapes
// ---------------------------------------------------------------------------

export interface RegisterBeginResponse {
  options: PublicKeyCredentialCreationOptionsJSON;
  /**
   * True when the server resolved this ceremony as APPENDING a credential to
   * an already-authenticated session's own account (`req.session.userId` was
   * already set), rather than creating a brand-new profile — the SAME
   * discriminator server/routes/webauthn-register.routes.ts uses. Drives the
   * #1620/#1621 vault-KEK-overwrite guard: never derive/send a
   * vaultKekEncrypted on this branch (see index.tsx
   * performRegisterDeriveAndComplete). Authoritative — never inferred from
   * client-side auth state, since the /auth route redirect that normally
   * keeps an authenticated user off this form is a client-only, racy guard.
   */
  isAppend: boolean;
}

export interface AuthBeginResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
  /** hex-encoded 32-byte PRF evaluation salt for this user */
  prfSalt: string;
}

export interface RegisterCompleteResponse {
  userId:    string;
  username:  string;
  aal:       AalLevel;
  /**
   * True when the just-registered account has NO wrapped vault KEK on file
   * (the authenticator did not evaluate the PRF extension and no prior
   * credential had wrapped one). Registration still succeeded; vault features
   * are unavailable on this device. Drives an honest, non-blocking Amber
   * notice (#1547). Absent/false → normal success.
   */
  prfUnavailable?: boolean;
}

export interface AuthCompleteResponse {
  userId:    string;
  username:  string;
  aal:       AalLevel;
  /** Claim UUID, present only when a guardian-initiated recovery claim is open for this account; the server guard blocks every other write until keep/veto. */
  pendingRecoveryClaim?: string;
}

/** Route for the ward-facing keep/veto interrupt page (Task 5b, AC-12). */
export const CLAIM_PENDING_PATH = "/recovery/claim-pending";

/**
 * Returns the default landing route for an authenticated assurance level.
 *
 * Lives in this light, component-free module (not auth-entry/index.tsx) so
 * that importing it — e.g. from claim-pending.tsx's non-AuthEntry landing
 * fallback — never drags the AuthEntry page chunk along (Task 5b fix round
 * 1, minor 10). Re-exported from index.tsx so existing importers of the
 * AuthEntry module keep working unchanged.
 */
export function routeForAal(aal: AalLevel): string {
  if (aal === "AAL1") return "/setup";
  if (aal === "AAL2") return "/dashboard";
  return "/security-centre";
}

// ---------------------------------------------------------------------------
// Animation constants (shared with sub-components)
// ---------------------------------------------------------------------------

export const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
} as const;

export const PAGE_TRANSITION = { duration: 0.22, ease: "easeOut" } as const;

export const SUCCESS_SPRING = { type: "spring", stiffness: 460, damping: 28 } as const;

export { PULSE_ANIMATE, PULSE_TRANSITION } from "@/lib/breathing-pulse";
